import { GET_STARTED_STEPS } from '@/config/static/workspace';
import type { FlowId } from '@/flows/types';
import { ONBOARDING_PROGRESS_CHANGED_EVENT, STORAGE_KEYS } from '@/utils/constants';

export type OnboardingStepId = (typeof GET_STARTED_STEPS)[number]['id'];

const VALID_STEP_IDS = new Set<string>(GET_STARTED_STEPS.map((s) => s.id));

const FLOW_TO_ONBOARDING_STEP: Partial<Record<FlowId, OnboardingStepId>> = {
  'device-setup': 'device',
  'user-creation': 'user',
  'access-group-creation': 'access',
  'bulk-user-import': 'user',
};

export function onboardingStepForFlow(flowId: FlowId): OnboardingStepId | undefined {
  return FLOW_TO_ONBOARDING_STEP[flowId];
}

function readCompleted(): OnboardingStepId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED_STEPS);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (id): id is OnboardingStepId => typeof id === 'string' && VALID_STEP_IDS.has(id)
    );
  } catch {
    return [];
  }
}

function writeCompleted(steps: OnboardingStepId[]): void {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED_STEPS, JSON.stringify(steps));
  window.dispatchEvent(new CustomEvent(ONBOARDING_PROGRESS_CHANGED_EVENT));
}

export function getCompletedOnboardingSteps(): OnboardingStepId[] {
  return readCompleted();
}

export function getOnboardingCompletedCount(): number {
  return readCompleted().length;
}

export function isOnboardingStepComplete(stepId: string): boolean {
  return readCompleted().includes(stepId as OnboardingStepId);
}

export function markOnboardingStepComplete(stepId: OnboardingStepId): void {
  const current = readCompleted();
  if (current.includes(stepId)) return;
  writeCompleted([...current, stepId]);
}
